import { useTranslation } from 'react-i18next';
import { Form, FormItem } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Flex } from 'antd';
import { CalendarOutline, Slash } from '../icons';
import CustomDatePicker from '../Ant/CustomDatePicker/CustomDatePicker';
import { TimePicker } from '../Ant/TimePicker';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { ICreateWorkTimeDto, IUpdateWorkTimeDto, IWorkTime } from '../../interface/workTimes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import dayjs from 'dayjs';
import {
  chosenTimeOfDay,
  extractDate,
  extractTime,
  getChangedFields,
  mergeDateAndTime,
} from '../../constants/utilityFunctions';
import AddDuration from './addDuration';
import WorkTimeActivitySelect from './workTimeActivitySelect';
import WorkTimeMatterSelect from './workTimeMatterSelect';
import WorkTimeTypeSelect from './workTimeTypeSelect';
import { useCreateWorktime } from '../../hooks/useCreateWorktime';
import { useUser } from '../../context/UserContext';
import { useParams } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { useFetchWorkTimeDetail } from '../../hooks/useFetchWorkTimeDetail';
import { useUpdateWorktime } from '../../hooks/useUpdateWorktime';
import { useVersionConflict } from '../../hooks/useVersionConflict';
import { fetchRecordVersion } from '../../helpers/versionConflictHelper';
import { API_URLS } from '../../constants/apiUrl';
import { WORK_TIME_QUERY_KEYS_TO_INVALIDATE } from '../../constants/workTime';
import { showToast } from '../utils/toast';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import { useWorkTimeDefaults } from '../../hooks/useWorkTimeDefaults';
import CustomModal from '../informationPopup/customModal';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { isWorkTimeFormChanged } from './isWorkTimeFormChanged';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

const AddEditWorktime = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { id } = useParams();
  const { user } = useUser();
  const { workTime, updateWorkTime, initialWorkTime } = useWorkTime();
  const { goBack, push } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const { trackPage } = useAnalytics();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const createWorkTimeMutation = useCreateWorktime();
  const updateWorkTimeMutation = useUpdateWorktime();
  const { latestVersion, handleVersionConflict } = useVersionConflict({
    fetchLatestVersion: async () =>
      id ? fetchRecordVersion(`${API_URLS.WORK_TIMES}/${id}`) : undefined,
    // The work time detail is not invalidated: the changed fields are diffed against it, so a refetch would turn
    // the fields the user did not touch into their changes and overwrite what was edited elsewhere
    queriesToInvalidate: WORK_TIME_QUERY_KEYS_TO_INVALIDATE,
  });

  useWorkTimeDefaults({
    enabled: !workTime?.workTimeType && !id && !workTime?.isWorkTimeTypeManuallyCleared,
  });

  // Auto-update subject when workTimeType changes and subject hasn't been manually edited
  useEffect(() => {
    if (workTime?.workTimeType && !workTime?.isSubjectManuallyEdited && !id) {
      const workTimeTypeTranslation = getTranslationFromTranslatableEntity(
        workTime.workTimeType.translations,
        getAppLanguageCode(),
      );
      if (workTimeTypeTranslation && workTime.subject !== workTimeTypeTranslation) {
        updateWorkTime({
          ...workTime,
          subject: workTimeTypeTranslation,
        });
      }
    }
  }, [workTime?.workTimeType?.id, workTime?.isSubjectManuallyEdited, id]);

  const { data: workTimeDetail, isLoading: isLoadingWorkTime } = useFetchWorkTimeDetail({
    id,
    relations: [
      'matter',
      'activity',
      'transaction',
      'workTimeType',
      'company',
      'privatePerson',
      'workTimeUser',
    ],
    fields: [
      'startTime',
      'endTime',
      'workTimeUserId',
      'activityId',
      'duration',
      'subject',
      'description',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.WORK_TIME_ADD_EDIT);
  }, [trackPage]);
  const onBack = () => {
    validateOnFormChanged(() => {
      handleBack();
    });
  };
  const validateOnFormChanged = (onProceed: () => void) => {
    const isFormChanged = isWorkTimeFormChanged(workTime, initialWorkTime, !!id);
    setShowDiscardDialog(isFormChanged);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  const handleBack = () => {
    updateWorkTime(null);
    goBack();
  };
  useEffect(() => {
    if (!isLoadingWorkTime) {
      setTitle(
        <AddEditTransactionHeader
          title={id ? (workTimeDetail?.subject ?? '') : t('worktime:addWorkTime')}
          handleBack={onBack}
          showCross
          validateOnFormChanged={validateOnFormChanged}
          subTitle={id ? t('worktime:editWorkTime') : ''}
        />,
      );
    }
  }, [workTime, id, isLoadingWorkTime]);

  useEffect(() => {
    if (workTimeDetail && !isLoadingWorkTime && !workTime?.id) {
      updateWorkTime({
        ...workTimeDetail,
      } as IWorkTime);
    }
  }, [workTimeDetail, isLoadingWorkTime]);

  const handleWorkTimeType = () => {
    push(ROUTES.WORK_TIME_TYPE, { returnFocusTo: 'workTimeType' });
  };
  const handleSelectMatter = () => {
    push(ROUTES.MATTER_SELECTION, {
      isFromWorkTime: true,
      returnFocusTo: 'matter',
    });
  };
  const handleSelectActivity = () => {
    push(ROUTES.ACTIVITY_SELECTION, { returnFocusTo: 'activity' });
  };
  const handleCreateWorkTime = () => {
    const basePayload: ICreateWorkTimeDto = {
      subject: workTime?.subject ?? '',
      startTime: workTime?.startTime ?? '',
      endTime: workTime?.endTime ?? '',
      workTimeUserId: user?.id ?? '',
      activityId: workTime?.activity?.id ?? undefined,
      matterId: workTime?.matter?.id ?? undefined,
      description: workTime?.description ?? undefined,
      workTimeTypeId: workTime?.workTimeType?.id ?? undefined,
    };
    createWorkTimeMutation.mutate(basePayload, {
      onSuccess: () => {
        handleBack();
      },
    });
  };
  const handleUpdateWorkTime = () => {
    const basePayload: IUpdateWorkTimeDto = {
      subject: workTime?.subject ?? '',
      startTime: workTime?.startTime ?? '',
      endTime: workTime?.endTime ?? '',
      activityId: workTime?.activity?.id ?? null,
      matterId: workTime?.matter?.id ?? null,
      description: workTime?.description ?? undefined,
      workTimeTypeId: workTime?.workTimeType?.id ?? undefined,
      workTimeUserId: user?.id ?? '',
    };
    const changedPayload = getChangedFields(
      workTimeDetail as unknown as IWorkTime,
      basePayload as unknown as IWorkTime,
    );
    updateWorkTimeMutation.mutate(
      { ...changedPayload, version: latestVersion ?? workTime?.version, id },
      {
        onSuccess: () => {
          handleBack();
        },
        onError: handleVersionConflict,
      },
    );
  };
  const handleSave = () => {
    if (id) {
      handleUpdateWorkTime();
    } else {
      handleCreateWorkTime();
    }
  };
  const validateAndAdjustTimeRange = (
    start: Date | string | null | undefined,
    end: Date | string | null | undefined,
  ) => {
    let startDate: Date | undefined;
    let endDate: Date | undefined;
    if (start) {
      startDate = new Date(start);
    }
    if (end) {
      endDate = new Date(end);
    }

    if (startDate && endDate && startDate > endDate) {
      showToast('info', t('worktime:endDateCannotBeBeforeStartDate'));
      endDate = startDate;
    }

    const updatedWorkTime: Partial<IWorkTime> = { ...workTime };

    if (startDate) {
      updatedWorkTime.startTime = startDate.toISOString();
    }
    if (endDate) {
      updatedWorkTime.endTime = endDate.toISOString();
    }

    updateWorkTime(updatedWorkTime as IWorkTime);
  };

  return (
    <div className="h-full pb-gutter w-full">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <div className="w-full relative">
            <FormItem label={t('common:subject')} required data-testid="worktime-subject">
              <TextAreaField
                data-testid="worktime-subject-input"
                maxLength={1000}
                value={workTime?.subject ?? ''}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateWorkTime({
                    ...workTime,
                    subject: e?.target?.value ?? '',
                    isSubjectManuallyEdited: true,
                  } as IWorkTime);
                }}
              />
            </FormItem>
          </div>
          <FormItem label={t('worktime:workTimeType')}>
            <div className="relative">
              <WorkTimeTypeSelect
                onOpenPicker={handleWorkTimeType}
                onClear={() =>
                  updateWorkTime({
                    ...workTime,
                    workTimeType: undefined,
                    workTimeTypeId: undefined,
                    isWorkTimeTypeManuallyCleared: true,
                  } as unknown as IWorkTime)
                }
              />
            </div>
          </FormItem>
          <Flex className="w-full gap-gutter">
            <FormItem
              data-testid="worktime-start-date"
              className="w-full"
              label={t('worktime:startDate')}
              required={true}
            >
              <CustomDatePicker
                data-testid="worktime-start-date-picker"
                className="w-full"
                placeholder={t('worktime:selectStartDate')}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                value={workTime?.startTime ? dayjs(workTime.startTime) : undefined}
                onChange={(date) => {
                  const newStartTime = mergeDateAndTime(
                    date?.toDate(),
                    extractTime(workTime?.startTime)!,
                  );
                  validateAndAdjustTimeRange(newStartTime, workTime?.endTime);
                }}
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem
              data-testid="worktime-start-time"
              required={true}
              className="w-full"
              label={t('worktime:startTime')}
            >
              <TimePicker
                data-testid="worktime-start-time-picker"
                className="w-full"
                format="HH:mm"
                placeholder={t('worktime:selectStartTime')}
                allowClear={false}
                value={chosenTimeOfDay(workTime?.startTime, !!id)}
                onChange={(time) => {
                  const newStartTime = mergeDateAndTime(
                    extractDate(workTime?.startTime)!,
                    time?.toDate(),
                  );
                  validateAndAdjustTimeRange(newStartTime, workTime?.endTime);
                }}
                placement="bottomLeft"
                classNames={{ popup: { root: 'custom-time-picker' } }}
                getPopupContainer={(trigger) =>
                  (trigger.parentNode as HTMLElement) || document.body
                }
                minuteStep={1}
                showNow={false}
              />
            </FormItem>
          </Flex>
          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('worktime:endDate')} required={true}>
              <CustomDatePicker
                data-testid="worktime-end-date-picker"
                className="w-full"
                placeholder={t('worktime:selectEndDate')}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                value={workTime?.endTime ? dayjs(extractTime(workTime?.endTime)) : undefined}
                onChange={(date) => {
                  const newEndTime = mergeDateAndTime(
                    date?.toDate(),
                    extractTime(workTime?.endTime)!,
                  );
                  validateAndAdjustTimeRange(workTime?.startTime, newEndTime);
                }}
                disabledDate={(current) => {
                  return current?.isBefore(dayjs(workTime?.startTime), 'day') ?? false;
                }}
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem required={true} className="w-full" label={t('worktime:endTime')}>
              <TimePicker
                data-testid="worktime-end-time-picker"
                className="w-full"
                format="HH:mm"
                placeholder={t('worktime:selectEndTime')}
                allowClear={false}
                placement="bottomLeft"
                classNames={{ popup: { root: 'custom-time-picker' } }}
                disabledTime={() => {
                  if (!workTime?.startTime) {
                    return {
                      disabledHours: () => [],
                      disabledMinutes: () => [],
                    };
                  }

                  const startTime = dayjs(workTime?.startTime);
                  const startHour = startTime.hour();
                  const startMinute = startTime.minute();

                  return {
                    disabledHours: () => [...new Array(startHour).keys()],
                    disabledMinutes: (selectedHour: number) => {
                      if (selectedHour === startHour) {
                        return [...new Array(startMinute + 1).keys()];
                      }
                      return [];
                    },
                  };
                }}
                value={workTime?.endTime ? dayjs(extractTime(workTime?.endTime)) : undefined}
                onChange={(time) => {
                  const newEndTime = mergeDateAndTime(
                    extractDate(workTime?.endTime)!,
                    time?.toDate(),
                  );
                  validateAndAdjustTimeRange(workTime?.startTime, newEndTime);
                }}
                getPopupContainer={(trigger) =>
                  (trigger.parentNode as HTMLElement) || document.body
                }
                minuteStep={1}
                showNow={false}
              />
            </FormItem>
          </Flex>
          <AddDuration />
          <FormItem label={t('common:description')}>
            <TextAreaField
              data-testid="worktime-description-input"
              maxLength={1000}
              placeholder={t('common:addDescription')}
              value={workTime?.description ?? ''}
              onChange={(e) => {
                updateWorkTime({
                  ...workTime,
                  description: e?.target?.value ?? '',
                } as IWorkTime);
              }}
            />
          </FormItem>
          <FormItem name="matter" label={t('matter:matter')}>
            <div className="relative">
              <WorkTimeMatterSelect
                onOpenPicker={handleSelectMatter}
                onClear={() =>
                  updateWorkTime({
                    ...workTime,
                    matter: undefined,
                    matterId: undefined,
                  } as unknown as IWorkTime)
                }
              />
            </div>
          </FormItem>
          <FormItem name="activity" label={t('worktime:activity')}>
            <div className="relative">
              <WorkTimeActivitySelect
                onOpenPicker={handleSelectActivity}
                onClear={() =>
                  updateWorkTime({
                    ...workTime,
                    activity: undefined,
                    activityId: undefined,
                  } as unknown as IWorkTime)
                }
              />
            </div>
          </FormItem>
          <PinnedSaveButton
            data-testid="worktime-save-button"
            disabled={!workTime?.subject || !workTime?.startTime || !workTime?.endTime}
            onClick={handleSave}
          >
            {id ? t('common:saveChanges') : t('worktime:addWorkTime')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default AddEditWorktime;
